import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const inputCls = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:outline-none focus:border-blue-500";

/**
 * Lightweight in-memory cache so multiple <MasterSelect> instances on the same
 * form don't trigger redundant requests for the same list.
 */
const cache = new Map();
const inflight = new Map();

async function fetchList(key) {
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);
  const p = api.get(`/fuhrpark-master/${key}`)
    .then(({ data }) => { cache.set(key, data || []); inflight.delete(key); return data || []; })
    .catch(() => { inflight.delete(key); return []; });
  inflight.set(key, p);
  return p;
}

export function invalidateMasterList(key) { cache.delete(key); }

/**
 * Generic master-data <select>.
 * - listKey:   key in the fuhrpark-master registry (e.g. "vehicle_brands")
 * - valueField: object property used for the value/option text (default "name", or "company")
 * - filter:    optional fn(item) → bool (for FK-filtering, e.g. models by brand)
 * - allowFree: when true and current value is not in list, render it as a fallback option
 *              so legacy free-text data still displays correctly.
 */
export default function MasterSelect({
  listKey, valueField = "name", value, onChange, placeholder = "— Bitte wählen —",
  filter = null, disabled = false, testid, allowFree = true, className = "",
}) {
  const [items, setItems] = useState([]);
  useEffect(() => { fetchList(listKey).then(setItems); }, [listKey]);

  const visible = useMemo(() => {
    let out = items;
    if (filter) out = out.filter(filter);
    return out;
  }, [items, filter]);

  const valueInList = useMemo(() => {
    if (!value) return true;
    // For id-based FK lists we treat value as id; otherwise compare against the value field
    return visible.some((it) => String(it[valueField]) === String(value) || String(it.id) === String(value));
  }, [visible, value, valueField]);

  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      className={`${inputCls} ${className}`}
    >
      <option value="">{placeholder}</option>
      {visible.map((it) => (
        <option key={it.id} value={it[valueField]}>{it[valueField]}</option>
      ))}
      {/* Backward-compat: keep legacy free-text values selectable */}
      {allowFree && value && !valueInList && (
        <option value={value}>{value} (alt)</option>
      )}
    </select>
  );
}
